import React, { useState, useEffect, useCallback } from 'react';
import { Eye, AlertCircle, NotepadText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useApp } from '../../contexts/AppProvider';
import API_CONFIG from '../../config/api';

const BASE_URL = API_CONFIG.BASE_URL;


// Add this component inside your file, before the main export
const MiniSurveyPreview = ({ logoUrl }) => {
	return (
	  <div 
		className="w-full mt-2 p-4 rounded shadow-inner relative"
		style={{ 
		  backgroundColor: 'white',
		  border: '1px solid #e5e7eb'
		}}
	  >
		{/* Logo in top right corner */}
		{logoUrl && (
		  <div className="absolute top-2 right-2">
			<img 
			  src={logoUrl} 
			  alt="Survey Logo" 
			  className="h-6 w-auto object-contain"
			  style={{ maxWidth: '60px' }}
			/>
		  </div>
		)}
		
		<div className="space-y-3 text-left">
		  {/* Mini survey title */}
		  <div className="text-xs font-semibold" style={{ color: '#374151' }}>
			Survey Preview
		  </div>
		  
		  {/* Question 1 */}
		  <div className="space-y-1">
			<div className="text-xs font-medium" style={{ color: '#6b7280' }}>
			  1. ...
			</div>
			<div className="flex gap-2">
			  {[1, 2, 3, 4, 5].map((num) => (
				<div
				  key={num}
				  className="w-6 h-6 rounded flex items-center justify-center text-xs"
				  style={{ 
					backgroundColor: '#f3f4f6',
					color: '#9ca3af',
					fontSize: '10px'
				  }}
				>
				  {num}
				</div>
			  ))}
			</div>
		  </div>
		  
		  {/* Question 2 */}
		  <div className="space-y-1">
			<div className="text-xs font-medium" style={{ color: '#6b7280' }}>
			  2. ...
			</div>
			<div 
			  className="w-full h-8 rounded text-xs flex items-center px-2"
			  style={{ 
				backgroundColor: '#f9fafb',
				border: '1px solid #e5e7eb',
				color: '#d1d5db'
			  }}
			>
			  Your feedback...
			</div>
		  </div>
		  
		  {/* Mini submit button */}
		  <div className="flex justify-end pt-1">
			<div 
			  className="px-3 py-1 rounded text-xs font-medium"
			  style={{ 
				backgroundColor: '#e5e7eb',
				color: '#6b7280'
			  }}
			>
			  Submit
			</div>
		  </div>
		</div>
	  </div>
	);
  };

export default function SurveyPreviewFeature({ featureName }) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const { tenantInfos, t } = useApp();

  const handleOpenSurveyPreview = () => {
	sessionStorage.setItem(`surveyPreviewData_${tenantInfos.interfaceId}_${tenantInfos.accessToken}`, JSON.stringify({
	  survey: surveyData.survey,
	  theme: surveyData.survey_theme
	}));
	window.open(`/${tenantInfos.interfaceId}/${tenantInfos.accessToken}/survey-preview`, '_blank');
  };

  const fetchFeatureData = useCallback(async () => {
	setIsLoading(true);
	setError(null);

	try {
	  const tenant_id = tenantInfos.interfaceId
	  const access_token = tenantInfos.accessToken
	  const api_url = `${BASE_URL}/cui_portal/${tenant_id}/${access_token}/features/get_data/${featureName}`;
	  
	  const result = await fetch(api_url);
	  
	  if (result.status === 404) {
		setError({
		  statusCode: 404,
		  detail: "Feature data not found"
		});
		return;
	  }

	  if (result.status === 401) {
		setError({
		  statusCode: 401,
		  detail: "You are not authorized to access this feature.\nPlease contact your administrator"
		});
		return;
	  }

	  if (!result.ok) {
		setError({
		  statusCode: 500,
		  detail: "Unexpected error happened"
		});
		return;
	  }

	  const response = await result.json();
	  
	  if (response.success && response.data) {
		setSurveyData(response.data);
		setError(null);
	  } else if (response.error) {
		setError({
		  statusCode: response.error.status_code,
		  detail: response.error.detail || response.error.message
		});
	  } else {
		setError({
		  statusCode: 422,
		  detail: "Unexpected data structure"
		});
	  }
	} catch (err) {
	  console.error("Error fetching feature data:", err);
	  setError({
		statusCode: 500,
		detail: "Failed to fetch feature data"
	  });
	} finally {
	  setIsLoading(false);
	}
  }, [featureName]);

  useEffect(() => {
	fetchFeatureData();
  }, [fetchFeatureData]);

  // Loading state
  if (isLoading) {
	return (
	  <div className="max-w-7xl mx-auto px-6 py-12">
		<div className="flex items-center justify-center min-h-[400px]">
		  <div className="text-center">
			<div 
			  className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
			  style={{ borderColor: 'var(--color-primary)' }}
			/>
			<p style={{ color: 'var(--color-muted)' }}>Loading survey data...</p>
		  </div>
		</div>
	  </div>
	);
  }

  // Error state
  if (error) {
	return (
	  <div className="max-w-7xl mx-auto px-6 py-12">
		<div className="flex items-center justify-center min-h-[400px]">
		  <div 
			className="p-6 rounded-lg border"
			style={{ 
			  backgroundColor: 'color-mix(in srgb, red 10%, var(--color-surface))',
			  borderColor: 'color-mix(in srgb, red 30%, transparent)'
			}}
		  >
			<div className="flex items-start gap-3">
			  <AlertCircle className="h-6 w-6 flex-shrink-0" style={{ color: 'red' }} />
			  <div>
				<h3 className="font-semibold mb-1" style={{ color: 'var(--color-text)' }}>
				  Error {error.statusCode}
				</h3>
				<p style={{ color: 'var(--color-text)', whiteSpace: 'pre-line' }}>
				  {error.detail}
				</p>
			  </div>
			</div>
		  </div>
		</div>
	  </div>
	);
  }

  // Main content
  return (
	<div className=" mx-auto px-6 py-8">


		{/* Split Layout */}
		<div className="grid grid-cols-1 lg:grid-cols-[2fr_auto_1fr] gap-8">
		
			{/* Left Side - Description */}
			<div className="space-y-4">
				{/* Header for left side only */}
				<div className="flex items-end justify-between w-full">
				<div className="flex items-end gap-2">
					<NotepadText className="h-8 w-8 mb-1" style={{ color: 'var(--color-primary)' }} />
					<h1 className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
					{t("previewTitle")}
					</h1>
				</div>
				{surveyData?.created_at && (
					<p className="text-sm mb-1" style={{ color: 'var(--color-muted)' }}>
					Survey creation: {new Date(surveyData.created_at).toLocaleDateString()}
					</p>
				)}
				</div>

					<div 
					className="p-6 border"
					style={{ 
						backgroundColor: 'var(--color-surface)',
						borderColor: 'var(--color-muted)'
					}}
					>
						<div 
						className="max-w-none overflow-y-auto"
						style={{ 
							color: 'var(--color-text)',
							maxHeight: '600px',
						}}
						>
						<ReactMarkdown
							components={{
							h1: ({node, ...props}) => <h1 style={{fontSize: '2em', fontWeight: 'bold', marginBottom: '0.5em', color: 'var(--color-text)'}} {...props} />,
							h2: ({node, ...props}) => <h2 style={{fontSize: '1.5em', fontWeight: 'bold', marginBottom: '0.5em', color: 'var(--color-text)'}} {...props} />,
							h3: ({node, ...props}) => <h3 style={{fontSize: '1.25em', fontWeight: 'bold', marginBottom: '0.5em', color: 'var(--color-text)'}} {...props} />,
							p: ({node, ...props}) => <p style={{marginBottom: '1em', color: 'var(--color-text)'}} {...props} />
							}}
						>
							{surveyData?.description || '*No description available*'}
						</ReactMarkdown>
						</div>
					</div>
			</div>

				{/* Separator - only visible on large screens */}
				<div className="hidden lg:block w-px" style={{ backgroundColor: 'var(--color-muted)', opacity: 0.3 }} />

			{/* Right Side - Preview Button */}
			<div className="space-y-4 mt-12 pt-1">
  <div className="flex flex-col items-center justify-center">
    <button
      onClick={handleOpenSurveyPreview}
      className="w-full px-6 py-8 font-semibold text-lg transition-all duration-200 shadow-md hover:shadow-xl"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--color-primary) 50%, white)',
        color: 'var(--color-text)',
        transform: 'scale(1)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.02)';
        e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-primary) 55%, white)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-primary) 50%, white)';
      }}
    >
      <div className="flex flex-col items-center gap-3">
        <Eye className="h-10 w-10" style={{ color: 'var(--color-primary)' }} />
        Preview
        <p className="text-sm font-normal" style={{ color: 'var(--color-muted)' }}>
          <strong>{t("previewText")}</strong>
        </p>
        
        {/* Embedded mini survey preview */}
        <MiniSurveyPreview logoUrl={tenantInfos.logoUrl} />
      </div>
    </button>
  </div>
</div>
		</div>
	</div>
  );
}